import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../lib/api';
import { Plus, Trash2, Pencil, X, Tag, Percent, DollarSign, Calendar, Hash, ToggleLeft, ToggleRight } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';

export default function AdminPromoTab() {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ code: '', discount_type: 'percentage', value: '', min_order: '', max_uses: '', expires_at: '' });

  const load = () => {
    setLoading(true);
    adminAPI.getPromoCodes().then(r => setCodes(r.data.promocodes)).catch(() => toast.error('Erreur chargement')).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const resetForm = () => {
    setForm({ code: '', discount_type: 'percentage', value: '', min_order: '', max_uses: '', expires_at: '' });
    setEditing(null);
    setShowForm(false);
  };

  const handleSubmit = async () => {
    if (!form.code || !form.value) { toast.error('Code et valeur requis'); return; }
    try {
      if (editing) {
        await adminAPI.updatePromoCode(editing, { ...form, value: parseFloat(form.value), min_order: parseFloat(form.min_order || 0), max_uses: parseInt(form.max_uses || 0) });
        toast.success('Code promo mis à jour');
      } else {
        await adminAPI.createPromoCode({ ...form, value: parseFloat(form.value), min_order: parseFloat(form.min_order || 0), max_uses: parseInt(form.max_uses || 0) });
        toast.success('Code promo créé');
      }
      resetForm();
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce code promo ?')) return;
    try { await adminAPI.deletePromoCode(id); toast.success('Supprimé'); load(); } catch { toast.error('Erreur'); }
  };

  const handleToggle = async (promo) => {
    try {
      await adminAPI.updatePromoCode(promo.id, { active: !promo.active });
      toast.success(promo.active ? 'Désactivé' : 'Activé');
      load();
    } catch { toast.error('Erreur'); }
  };

  const startEdit = (promo) => {
    setForm({
      code: promo.code, discount_type: promo.discount_type, value: String(promo.value),
      min_order: String(promo.min_order || ''), max_uses: String(promo.max_uses || ''),
      expires_at: promo.expires_at ? promo.expires_at.split('T')[0] : '',
    });
    setEditing(promo.id);
    setShowForm(true);
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-[#033871]">Codes Promo</h2>
        <Button onClick={() => { resetForm(); setShowForm(true); }} className="bg-[#0C63BF] hover:bg-[#033871] gap-2 h-9 text-sm" data-testid="add-promo-btn">
          <Plus className="w-4 h-4" /> Nouveau code
        </Button>
      </div>

      {showForm && (
        <div className="bg-white rounded-2xl border border-[#E8F4FD] p-5 space-y-4" data-testid="promo-form">
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-[#033871] text-sm">{editing ? 'Modifier le code' : 'Nouveau code promo'}</h3>
            <button onClick={resetForm} className="text-[#5A8BA8] hover:text-[#033871]"><X className="w-4 h-4" /></button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-[#5A8BA8] mb-1 block">Code</label>
              <input value={form.code} onChange={e => setForm(p => ({ ...p, code: e.target.value.toUpperCase() }))}
                placeholder="ex: PADEL10" className="w-full h-10 px-3 rounded-xl border border-[#D6E8F0] text-sm font-bold uppercase focus:border-[#0C63BF] outline-none" data-testid="promo-code-input" />
            </div>
            <div>
              <label className="text-xs font-medium text-[#5A8BA8] mb-1 block">Type de réduction</label>
              <select value={form.discount_type} onChange={e => setForm(p => ({ ...p, discount_type: e.target.value }))}
                className="w-full h-10 px-3 rounded-xl border border-[#D6E8F0] text-sm focus:border-[#0C63BF] outline-none" data-testid="promo-type-select">
                <option value="percentage">Pourcentage (%)</option>
                <option value="fixed">Montant fixe (€)</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-[#5A8BA8] mb-1 block">Valeur ({form.discount_type === 'percentage' ? '%' : '€'})</label>
              <input type="number" value={form.value} onChange={e => setForm(p => ({ ...p, value: e.target.value }))}
                placeholder={form.discount_type === 'percentage' ? '10' : '5.00'} className="w-full h-10 px-3 rounded-xl border border-[#D6E8F0] text-sm focus:border-[#0C63BF] outline-none" data-testid="promo-value-input" />
            </div>
            <div>
              <label className="text-xs font-medium text-[#5A8BA8] mb-1 block">Commande minimum (€)</label>
              <input type="number" value={form.min_order} onChange={e => setForm(p => ({ ...p, min_order: e.target.value }))}
                placeholder="0 = pas de minimum" className="w-full h-10 px-3 rounded-xl border border-[#D6E8F0] text-sm focus:border-[#0C63BF] outline-none" data-testid="promo-minorder-input" />
            </div>
            <div>
              <label className="text-xs font-medium text-[#5A8BA8] mb-1 block">Utilisations max</label>
              <input type="number" value={form.max_uses} onChange={e => setForm(p => ({ ...p, max_uses: e.target.value }))}
                placeholder="0 = illimité" className="w-full h-10 px-3 rounded-xl border border-[#D6E8F0] text-sm focus:border-[#0C63BF] outline-none" data-testid="promo-maxuses-input" />
            </div>
            <div>
              <label className="text-xs font-medium text-[#5A8BA8] mb-1 block">Date d'expiration</label>
              <input type="date" value={form.expires_at} onChange={e => setForm(p => ({ ...p, expires_at: e.target.value }))}
                className="w-full h-10 px-3 rounded-xl border border-[#D6E8F0] text-sm focus:border-[#0C63BF] outline-none" data-testid="promo-expires-input" />
            </div>
          </div>
          <div className="flex gap-2 pt-1">
            <Button onClick={resetForm} variant="outline" className="h-9 text-sm">Annuler</Button>
            <Button onClick={handleSubmit} className="bg-[#0C63BF] hover:bg-[#033871] h-9 text-sm" data-testid="promo-submit-btn">
              {editing ? 'Mettre à jour' : 'Créer le code'}
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-10"><div className="w-8 h-8 border-3 border-[#0C63BF] border-t-transparent rounded-full animate-spin" /></div>
      ) : codes.length === 0 ? (
        <div className="bg-white rounded-2xl border border-[#E8F4FD] p-10 text-center">
          <Tag className="w-10 h-10 text-[#B8D4E3] mx-auto mb-3" />
          <p className="text-sm text-[#5A8BA8]">Aucun code promo. Cliquez sur "Nouveau code" pour en créer un.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {codes.map(promo => (
            <div key={promo.id} className={`bg-white rounded-2xl border p-4 flex flex-col sm:flex-row sm:items-center gap-3 ${promo.active ? 'border-[#E8F4FD]' : 'border-red-100 bg-red-50/30'}`} data-testid={`promo-${promo.code}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-black text-[#033871] text-base tracking-wider">{promo.code}</span>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${promo.active ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-600'}`}>
                    {promo.active ? 'Actif' : 'Inactif'}
                  </span>
                  <span className="text-xs font-semibold text-[#0C63BF] bg-[#F0F7FA] px-2 py-0.5 rounded-full">
                    {promo.discount_type === 'percentage' ? `${promo.value}%` : `${promo.value}€`}
                  </span>
                </div>
                <div className="flex items-center gap-3 mt-1.5 text-[11px] text-[#5A8BA8] flex-wrap">
                  {promo.min_order > 0 && <span>Min: {promo.min_order}€</span>}
                  {promo.max_uses > 0 && <span><Hash className="w-3 h-3 inline" /> {promo.used_count}/{promo.max_uses} utilisations</span>}
                  {promo.max_uses === 0 && <span><Hash className="w-3 h-3 inline" /> {promo.used_count} utilisations</span>}
                  {promo.expires_at && <span><Calendar className="w-3 h-3 inline" /> Expire : {new Date(promo.expires_at).toLocaleDateString('fr-FR')}</span>}
                </div>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <button onClick={() => handleToggle(promo)} className={`p-2 rounded-lg transition-colors ${promo.active ? 'text-emerald-600 hover:bg-emerald-50' : 'text-red-500 hover:bg-red-50'}`}
                  data-testid={`promo-toggle-${promo.code}`} title={promo.active ? 'Désactiver' : 'Activer'}>
                  {promo.active ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
                </button>
                <button onClick={() => startEdit(promo)} className="p-2 rounded-lg text-[#5A8BA8] hover:bg-[#F0F7FA] hover:text-[#0C63BF] transition-colors" data-testid={`promo-edit-${promo.code}`}>
                  <Pencil className="w-4 h-4" />
                </button>
                <button onClick={() => handleDelete(promo.id)} className="p-2 rounded-lg text-[#5A8BA8] hover:bg-red-50 hover:text-red-500 transition-colors" data-testid={`promo-delete-${promo.code}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
